import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import BasicNavbar from '../../_components/atoms/BasicNavbar';
import PlaybookSideBar from '../../_components/atoms/PlaybookSideBar';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { useStores } from '../../stores';
import Snackbar from '../../_components/atoms/Snackbar';
import Loader from '../../_components/molecules/Loader';
import {
  PlaybookAddQuestion,
  PlaybookCompensationTransparencyFormatter,
  PlaybookGagClauseFormatter,
  PlaybookIgnoreClause,
  playbookRiskReportFormatter,
  playbookSampleClauseFormatter,
} from '../../core-utils/helper-utils/playBookHelper';
import RecommendationInfo from '../../_components/atoms/RecommendationInfo';
import { observer } from 'mobx-react';
import { scrollBarStyles } from '../../core-utils/messages/CommonConstants';
import PlaybookClauseDetails, {
  CheckOffQuestion,
} from '../../_components/organisms/PlaybookClauseDetails';
import {
  BasicAccordion,
  Button,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';

export interface PlaybookDetailsProps {}

const PlaybookDetailsPage: React.FC<PlaybookDetailsProps> = observer(() => {
  const useStyles = makeStyles()({
    root: {
      position: 'relative',
      backgroundColor: patientAccessTheme.palette.grey[100],
    },
    main: {
      display: 'flex',
    },
    content: {
      width: '800px',
      margin: '5rem',
      marginLeft: 'calc(400px + 3rem)',

      overflowY: 'auto',
      overflowX: 'hidden',
      ...scrollBarStyles,
    },
  });
  const { classes } = useStyles();
  const contractAiqStore = useStores().contractAiqStore;

  const [loader] = useState<any>(<Loader />);
   
  const [loading, setLoading] = useState<boolean>(false);
  const [playbookData, setPlaybookData] = useState<any[]>([]);
  const [filteredPlaybookData, setFilteredPlaybookData] = useState<any>([]);
  const [sidebarContent, setSidebarContent] = useState<any>([]);
  const [gagClauseInfo, setGagClauseInfo] = useState<any>();
  const [
    compensationTransparency,
    setCompensationTransparency,
  ] = useState<any>();
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };
  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const getPlaybookRiskDetails = async () => {
    setLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.riskReportClausesPlaybook,
        {
          eg_nid: contractAiqStore?.playbookData?.eg_nid,
          playbook_id: contractAiqStore?.playbookData?.playbook_id,
          typeOfPlaybook: contractAiqStore?.playbookData?.type_of_playbook,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          setPlaybookData(response.data?.data);
          setFilteredPlaybookData(response.data?.data);
          setSidebarContent(
            playbookRiskReportFormatter(
              response.data?.data,
              contractAiqStore?.playbookData?.type_of_playbook,
            ),
          );
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addPlaybookQuestion = async (
    standard_id: string,
    clause_id: number,
    question: string,
    category: string,
  ) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.addQuestion,
        {
          eg_nid: contractAiqStore?.playbookData?.eg_nid,
          playbook_id: contractAiqStore?.playbookData?.playbook_id,
          standard_id,
          clause_id,
          question,
          category,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          const updatedData = response.data?.data;
          setPlaybookData(PlaybookAddQuestion(playbookData, updatedData));
          handleOpenSnackBar('Question added successfully...', 'success', 5000);
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const changePlaybookQuestionCategory = async (
    standard_id: string,
    clause_id: number,
    question_index: number,
    category: string,
  ) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.changeCategory,
        {
          eg_nid: contractAiqStore?.playbookData?.eg_nid,
          playbook_id: contractAiqStore?.playbookData?.playbook_id,
          standard_id,
          clause_id,
          question_index,
          category,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          const updatedData = response.data?.data;
          setPlaybookData(PlaybookAddQuestion(playbookData, updatedData));
          handleOpenSnackBar(
            'Category of Question updated successfully...',
            'success',
            5000,
          );
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      )
      .finally(() => {
        setLoading(false);
      });
  };

  const ignorePlaybookClause = async (
    standard_id: string,
    clause_id: number,
    isIgnored: boolean,
  ) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.ignoreClause,
        {
          eg_nid: contractAiqStore?.playbookData?.eg_nid,
          playbook_id: contractAiqStore?.playbookData?.playbook_id,
          standard_id,
          clause_id,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          const updatedData = response.data?.data;
          setPlaybookData(PlaybookIgnoreClause(playbookData, updatedData));
          handleOpenSnackBar(
            `Clause ${isIgnored ? 'UnIgnored' : 'Ignored'} Successfully...`,
            'success',
            5000,
          );
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  const getPlaybookGagClauseInfo = async () => {
    setLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.gagClauseInfo,
        {
          eg_nid: contractAiqStore?.playbookData?.eg_nid,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          setGagClauseInfo(PlaybookGagClauseFormatter(response.data?.data));
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const getPlaybookCompensationTransparencyInfo = async () => {
    setLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.compensationTransparencyInfo,
        {
          eg_nid: contractAiqStore?.playbookData?.eg_nid,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          setCompensationTransparency(
            PlaybookCompensationTransparencyFormatter(response.data?.data),
          );
        }
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearchChange = (searchText: string) => {
    if (searchText === '') {
      setFilteredPlaybookData(playbookData);
      setSidebarContent(
        playbookRiskReportFormatter(
          playbookData,
          contractAiqStore?.playbookData?.type_of_playbook,
        ),
      );
    } else {
      const filtered = playbookData.filter((data: any) =>
        data?.standard_id.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredPlaybookData(filtered);
      setSidebarContent(
        playbookRiskReportFormatter(
          filtered,
          contractAiqStore?.playbookData?.type_of_playbook,
        ),
      );
    }
  };

  const PurchasingStandards = () => {
    return (
      <Grid className={classes.content}>
        {filteredPlaybookData?.map((data: any, index: number) => (
          <div
            style={{
              marginBottom: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <RecommendationInfo
              title={data?.standard_id}
              subTitle={data?.summary}
              objectives={Object.values(data?.standard_name?.objective)}
              tooltip={data?.standard_name?.importance}
              index={index}
            />
            <Button
              color="primary"
              height="36px"
              width="190px"
              label="+ Add a new Clause"
              styleProps={{ marginLeft: 'auto' }}
            />
            {data?.all_clauses.map((clause: any, idx: number) => (
              <PlaybookClauseDetails
                clauseName={clause.clause_name}
                standardId={data?.standard_id}
                clauseId={clause?.clause_id}
                aim={clause.aim_of_the_clause}
                checkOffList={clause.check_off_list}
                sampleClause={playbookSampleClauseFormatter(clause.sample)}
                addPlaybookQuestion={addPlaybookQuestion}
                changePlaybookQuestionCategory={changePlaybookQuestionCategory}
                isIgnored={clause?.ignore}
                ignorePlaybookClause={ignorePlaybookClause}
                key={idx}
                isEditable={true}
              />
            ))}
          </div>
        ))}
      </Grid>
    );
  };

  const GagClauseCompliance = () => {
    return (
      <Grid className={classes.content}>
        <Grid style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontSize="18px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            styleProps={{ marginBottom: '1rem' }}
          >
            Gag Clause Compliance Playbook
          </Typography>
          {gagClauseInfo?.gagCompliance?.map((clause: any) => (
            <Grid style={{ marginBottom: '2rem' }}>
              <Typography
                fontSize="14px"
                fontType="semiBold"
                textColor={patientAccessTheme.palette.text.highemphasis}
                styleProps={{ marginBottom: '0.5rem' }}
              >
                {clause.question}
              </Typography>
              <Typography
                fontSize="14px"
                fontType="regular"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                styleProps={{
                  textAlign: 'justify',
                }}
              >
                {clause.definition}
              </Typography>
            </Grid>
          ))}

          <Typography
            fontSize="14px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            styleProps={{ marginBottom: '0.5rem' }}
          >
            Key Dates and Responsibilities
          </Typography>
          {gagClauseInfo?.gagKeyValues?.map((clause: any) => (
            <Grid style={{ display: 'flex', gap: '0.5rem' }}>
              <Typography
                fontSize="14px"
                fontType="regular"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                styleProps={{
                  textAlign: 'justify',
                }}
              >
                {clause.key}:
              </Typography>
              <Typography
                fontSize="14px"
                fontType="semiBold"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                styleProps={{
                  textAlign: 'justify',
                }}
              >
                {clause.value}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Grid
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '60px',
            gap: '2rem',
          }}
        >
          {playbookData[0]?.topics?.map((data: any, index: number) => (
            <>
              <RecommendationInfo
                title={data?.standard_id}
                header="Topic"
                infoSection={data?.description}
                infoSectionTitle="Aim"
                index={index}
              />

              <BasicAccordion
                title={'Check off List'}
                content={
                  <Grid>
                    {data?.checkOfflist?.map((checkOff: any, idx: number) => (
                      <CheckOffQuestion
                        title={checkOff.question}
                        index={idx}
                        isEditable={false}
                      />
                    ))}
                  </Grid>
                }
                rootStyles={{ borderRadius: '8px' }}
                accordionDetailsStyles={{
                  padding: 0,
                  '& .MuiGrid-root': { width: '100%' },
                }}
              />
            </>
          ))}
        </Grid>
      </Grid>
    );
  };

  const CompensationTransparency = () => {
    return (
      <Grid className={classes.content}>
        <Grid style={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontSize="18px"
            fontType="semiBold"
            textColor={patientAccessTheme.palette.text.highemphasis}
            styleProps={{ marginBottom: '1rem' }}
          >
            Compensation Transparency Playbook
          </Typography>

          {compensationTransparency?.map((clause: any) => (
            <Grid style={{ marginBottom: '2rem' }}>
              <Typography
                fontSize="14px"
                fontType="semiBold"
                textColor={patientAccessTheme.palette.text.highemphasis}
                styleProps={{ marginBottom: '0.5rem' }}
              >
                {clause.question}
              </Typography>
              <Typography
                fontSize="14px"
                fontType="regular"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                styleProps={{
                  textAlign: 'justify',
                }}
              >
                {clause.definition}
              </Typography>
            </Grid>
          ))}
        </Grid>
        <Grid
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginTop: '40px',
            gap: '2rem',
          }}
        >
          {playbookData[0]?.topics?.map((data: any) => (
            <BasicAccordion
              title={data?.standard_id}
              content={
                <Grid>
                  <Typography
                    fontSize="14px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                  >
                    The clause focusing on "{data?.standard_id}" aims to:
                  </Typography>
                  <ul style={{ margin: '0 0 0 -20px' }}>
                    {data?.description.split('.,').map((aim: string) => (
                      <li>
                        <Typography
                          fontSize="14px"
                          fontType="regular"
                          textColor={
                            patientAccessTheme.palette.text.highemphasis
                          }
                          lineHeight="22px"
                        >
                          {aim}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                  <BasicAccordion
                    title={'Check off List'}
                    content={
                      <Grid>
                        {data?.checkOfflist?.map(
                          (checkOff: any, idx: number) => (
                            <CheckOffQuestion
                              title={checkOff.question}
                              index={idx}
                              isEditable={false}
                            />
                          ),
                        )}
                      </Grid>
                    }
                    rootStyles={{ marginTop: '1.5rem', borderRadius: '8px' }}
                    accordionDetailsStyles={{
                      padding: 0,
                      '& .MuiGrid-root': { width: '100%' },
                    }}
                  />
                </Grid>
              }
            />
          ))}
        </Grid>
      </Grid>
    );
  };

  const RegulatoryCompliance = () => {
    return (
      <Grid className={classes.content}>
        {playbookData[0]?.topics?.map((data: any, index: number) => (
          <div style={{ marginBottom: '60px' }}>
            <RecommendationInfo
              title={data?.standard_id}
              header="Topic"
              infoSection={data?.description}
              infoSectionTitle="About Topic"
              index={index}
            />
            <Grid style={{ margin: '1rem 0' }}>
              <Typography
                fontSize="14px"
                fontType="semiBold"
                textColor={patientAccessTheme.palette.text.highemphasis}
              >
                Exceptions
              </Typography>
              <ul style={{ margin: '0 0 0 -20px' }}>
                {data?.exceptions.map((exception: string) => (
                  <li>
                    <Typography
                      fontSize="14px"
                      fontType="regular"
                      lineHeight="26.6px"
                      textColor={patientAccessTheme.palette.text.mediumemphasis}
                    >
                      {exception}
                    </Typography>{' '}
                  </li>
                ))}
              </ul>
            </Grid>
            {data?.checkOfflist?.map((checkOff: any, idx: number) => (
              <BasicAccordion
                title={
                  'Question ' + (idx + 1) + ' - ' + checkOff?.question_title
                }
                content={
                  <Grid>
                    <Typography
                      fontSize="14px"
                      fontType="semiBold"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      lineHeight="26px"
                    >
                      {checkOff?.question}
                    </Typography>
                    <Typography
                      fontSize="14px"
                      fontType="regular"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      lineHeight="26px"
                    >
                      {checkOff?.summary}
                    </Typography>
                  </Grid>
                }
                rootStyles={{ borderRadius: '8px', margin: '1rem 0' }}
              />
            ))}
          </div>
        ))}
      </Grid>
    );
  };

  useEffect(() => {
    getPlaybookRiskDetails();

    if (
      contractAiqStore?.playbookData?.type_of_playbook ===
      'gag_clause_compliance'
    ) {
      getPlaybookGagClauseInfo();
    }

    if (
      contractAiqStore?.playbookData?.type_of_playbook ===
      'compensation_transparency'
    ) {
      getPlaybookCompensationTransparencyInfo();
    }
  }, []);

  return (
    <>
      {loading ? (
        loader
      ) : (
        <Grid container className={classes.root}>
          <BasicNavbar
            title={playbookData[0]?.playbook_name}
            acceptedBy={
              playbookData[0]?.user_name !== ''
                ? [playbookData[0]?.user_name]
                : []
            }
          />
          <Grid className={classes.main}>
            <PlaybookSideBar
              playbookData={sidebarContent}
              handleSearchChange={handleSearchChange}
            />

            {contractAiqStore.playbookData.type_of_playbook ===
              'purchasing_standards' && <PurchasingStandards />}
            {contractAiqStore.playbookData.type_of_playbook ===
              'gag_clause_compliance' && <GagClauseCompliance />}
            {contractAiqStore.playbookData.type_of_playbook ===
              'compensation_transparency' && <CompensationTransparency />}
            {contractAiqStore.playbookData.type_of_playbook ===
              'regulatory_compliance' && <RegulatoryCompliance />}
          </Grid>
        </Grid>
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
});

export default PlaybookDetailsPage;
